/**
 * Hook for validating slot availability in order edit mode
 * Tracks when a previously valid slot becomes invalid due to parameter changes
 */

import {useSecureApi} from '@hconnect/common/hooks/useApi'
import {AvailableSlotsResponse, SingleSlotParams} from '@hconnect/tuc-processes/slotManagement'
import moment from 'moment'
import {useEffect, useState, useMemo, useCallback, useRef} from 'react'
import {useQuery} from 'react-query'

import {useFeatureSlotManagementForRmcOrderIntakeEnabled} from './useFeatureFlag'

export type SlotValidationState = {
  isValid: boolean
  hasChanged: boolean
  needsReselection: boolean
  isLoading: boolean
  requiresDispatchApproval: boolean
  error?: string
}

export type SlotValidationResult = SlotValidationState & {
  resetValidation: () => void
}

export type SlotAffectingParams = Omit<SingleSlotParams, 'shippingDate'>

// Helper functions
const buildApiParams = (
  selectedSlotTime: string | undefined,
  orderParams: SlotAffectingParams
): SingleSlotParams => {
  const shippingDate = moment(selectedSlotTime || new Date())
    .startOf('day')
    .add(2, 'hours')
    .toISOString()

  return {shippingDate, ...orderParams}
}

const isQueryEnabled = (
  slotManagementEnabled: boolean,
  selectedSlotTime: string | undefined,
  params: SingleSlotParams
): boolean => {
  return !!(
    slotManagementEnabled &&
    selectedSlotTime &&
    params.siteNumber &&
    params.orgUnitId &&
    params.contractNumber &&
    params.contractItemNumber
  )
}

const checkSlotAvailableForInstantApproval = (
  selectedSlotTime: string | undefined,
  instantApprovalSlots: string[]
): boolean => {
  if (!selectedSlotTime) return true

  const selectedTime = moment(selectedSlotTime).format('HH:mm')
  return instantApprovalSlots.some((slot) => moment(slot).format('HH:mm') === selectedTime)
}

const detectParameterChanges = (
  initial: SlotAffectingParams,
  current: SlotAffectingParams
): boolean => {
  const keys: (keyof SlotAffectingParams)[] = [
    'contractNumber',
    'contractItemNumber',
    'siteNumber',
    'quantity',
    'unloadingMethod',
    'orgUnitId',
    'plusLoadExpected',
    'totalTimeOnJobSite',
    'flowRate',
    'loadSpacing'
  ]

  return keys.some((key) => initial[key] !== current[key])
}

/**
 * Hook for tracking slot validity in order edit mode
 * Monitors changes to order parameters that affect slot availability
 */
export const useSlotValidation = (
  selectedSlotTime: string | undefined,
  orderParams: SlotAffectingParams
): SlotValidationResult => {
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()
  const secureApi = useSecureApi()

  // Track initial state
  const initialStateRef = useRef<{
    initialized: boolean
    initiallyValid: boolean
    initialParams: SlotAffectingParams
  }>({
    initialized: false,
    initiallyValid: false,
    initialParams: orderParams
  })

  // Validation state
  const [validationState, setValidationState] = useState<SlotValidationState>({
    isValid: true,
    hasChanged: false,
    needsReselection: false,
    isLoading: false,
    requiresDispatchApproval: false
  })

  // Build API parameters
  const apiParams = useMemo(
    () => buildApiParams(selectedSlotTime, orderParams),
    [selectedSlotTime, orderParams]
  )

  // Check if query should be enabled
  const queryEnabled = useMemo(
    () => isQueryEnabled(slotManagementEnabled, selectedSlotTime, apiParams),
    [slotManagementEnabled, selectedSlotTime, apiParams]
  )

  // API call for slot validation
  const getSlots = useCallback(async (): Promise<AvailableSlotsResponse> => {
    try {
      const response = await secureApi.get('/rmcSlots/available', {params: apiParams})
      return response.data ?? {availableSlots: []}
    } catch (error) {
      return {availableSlots: []}
    }
  }, [secureApi, apiParams])

  // React Query for slot validation
  const slotValidationQuery = useQuery<AvailableSlotsResponse, Error>(
    ['slot-validation', apiParams],
    getSlots,
    {
      refetchInterval: false,
      cacheTime: 0,
      staleTime: 0,
      retry: 1,
      enabled: queryEnabled
    }
  )

  // Check if current slot is available for instant approval
  const isCurrentSlotAvailableForInstantApproval = useMemo(() => {
    if (!slotManagementEnabled || slotValidationQuery.isError) return true
    if (slotValidationQuery.isLoading) return validationState.isValid

    const instantApprovalSlots = slotValidationQuery.data?.availableSlots || []
    return checkSlotAvailableForInstantApproval(selectedSlotTime, instantApprovalSlots)
  }, [
    slotManagementEnabled,
    slotValidationQuery.isError,
    slotValidationQuery.isLoading,
    slotValidationQuery.data?.availableSlots,
    selectedSlotTime,
    validationState.isValid
  ])

  // Detect parameter changes
  const hasParamsChanged = useMemo(() => {
    if (!initialStateRef.current.initialized) return false
    return detectParameterChanges(initialStateRef.current.initialParams, orderParams)
  }, [orderParams])

  // Update validation state
  useEffect(() => {
    if (!slotManagementEnabled) {
      setValidationState({
        isValid: true,
        hasChanged: false,
        needsReselection: false,
        isLoading: false,
        requiresDispatchApproval: false
      })
      return
    }

    // Initialize on first run
    if (!initialStateRef.current.initialized && !slotValidationQuery.isLoading) {
      const isInstantApproval = isCurrentSlotAvailableForInstantApproval

      initialStateRef.current = {
        initialized: true,
        initiallyValid: isInstantApproval,
        initialParams: orderParams
      }

      setValidationState({
        isValid: true, // All slots are valid for selection
        hasChanged: false,
        needsReselection: false,
        isLoading: false,
        requiresDispatchApproval: !isInstantApproval
      })
      return
    }

    // Update validation state when slot availability or parameters change
    if (initialStateRef.current.initialized) {
      const wasInitiallyInstantApproval = initialStateRef.current.initiallyValid
      const isCurrentlyInstantApproval = isCurrentSlotAvailableForInstantApproval
      const paramsChanged = hasParamsChanged

      // Need reselection only if we went from instant approval to requiring dispatch approval due to param changes
      const needsReselection =
        wasInitiallyInstantApproval && !isCurrentlyInstantApproval && paramsChanged

      setValidationState({
        isValid: true, // All slots are always valid for selection
        hasChanged: paramsChanged,
        needsReselection,
        isLoading: slotValidationQuery.isLoading,
        requiresDispatchApproval: !isCurrentlyInstantApproval,
        error: needsReselection
          ? 'The selected time slot is no longer available for instant approval due to order changes. It will require dispatch approval.'
          : undefined
      })
    }
  }, [
    isCurrentSlotAvailableForInstantApproval,
    hasParamsChanged,
    slotValidationQuery.isLoading,
    slotManagementEnabled,
    orderParams
  ])

  // Reset validation callback
  const resetValidation = useCallback(() => {
    if (slotManagementEnabled && selectedSlotTime) {
      initialStateRef.current = {
        initialized: true,
        initiallyValid: true,
        initialParams: orderParams
      }

      setValidationState({
        isValid: true,
        hasChanged: false,
        needsReselection: false,
        isLoading: false,
        requiresDispatchApproval: false
      })
    }
  }, [slotManagementEnabled, selectedSlotTime, orderParams])

  return {
    ...validationState,
    resetValidation
  }
}
